import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { makeStyles, withStyles } from '@mui/styles';
import CustomInput from 'components/CustomInput/CustomInput.js';
import { useState } from 'react';

import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import emailMask from 'text-mask-addons/dist/emailMask';

import {
  primaryColor,
  secondaryColor,
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
  whiteColor,
} from '@themes/defaultThemeProps';
import {
  useAddClientTenantMutation,
  useUpdateClientTenantMutation,
} from 'api/client.api';
import { useDispatch, useSelector } from 'react-redux';
import { selectClientTenants } from '../../features/client/clientSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const emailMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  mask: emailMask,
};

const emailMaskCustom = (props) => {
  let { inputRef, ...other } = props;
  if (typeof inputRef !== 'function') inputRef = () => {};
  return (
    <MaskedInput
      {...other}
      {...emailMaskProps}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
    />
  );
};

const integerMask = createNumberMask({
  prefix: '',
  suffix: '',
  includeThousandsSeparator: false,
  allowDecimal: false,
  decimalLimit: 0,
  integerLimit: 3,
  allowNegative: false,
  allowLeadingZeroes: false,
});

const integerMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  mask: integerMask,
};

const integerMaskCustom = (props) => {
  let { inputRef, ...other } = props;
  if (typeof inputRef !== 'function') inputRef = () => {};
  return (
    <MaskedInput
      {...other}
      {...integerMaskProps}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
    />
  );
};

export default function ClientTenant(props) {
  const tenant = props.tenant
    ? {
        ...props.tenant,
        status: props.tenant.status === 'new' ? 'active' : props.tenant.status,
      }
    : {
        tenant_label: 'Production-' + props.tenantNumber,
        tenant_name: 'Production-' + props.tenantNumber,
        status: 'active',
        production: false,
        archival_period: 90,
        administrator_email: '',
      };

  const [label, setLabel] = useState(tenant.tenant_label);
  const [labelState, setLabelState] = useState('');
  const [labelError, setLabelError] = useState('');
  const [name, setName] = useState(tenant.tenant_name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [status, setStatus] = useState(tenant.status);
  const [production, setProduction] = useState(tenant.production);
  const [archivalPeriod, setArchivalPeriod] = useState(tenant.archival_period);
  const [administratorEmail, setAdministratorEmail] = useState(
    tenant.administrator_email
  );
  const [dirtyData, setDirtyData] = useState(false);
  const [updateClientTenant] = useUpdateClientTenantMutation();
  const [addClientTenant] = useAddClientTenantMutation();

  const title = props.new
    ? 'Enter New Environment Information'
    : 'Edit Environment Information';
  const submitLabel = props.new ? 'Create' : 'Save Changes';

  const classes = useStyles();
  const clientTenants = useSelector(selectClientTenants);

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) setDirtyData(true);
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) setDirtyData(true);
      return true;
    }
    return false;
  };

  const verifyLabel = (newLabel) => {
    if (verifyLength(newLabel, label, 3)) {
      if (tenant.tenant_label !== newLabel) {
        const index = clientTenants.findIndex(
          (ct) => ct.tenant_label === newLabel
        );
        if (index < 0) {
          setLabelError('');
          return true;
        } else {
          setLabelError(
            'Duplicate label already exists. Enter a different label.'
          );
          return false;
        }
      } else {
        setLabelError('');
        return true;
      }
    } else {
      setLabelError('The field should be at least 3 characters long');
      return false;
    }
  };

  const verifyName = (newName) => {
    if (verifyLength(newName, name, 3)) {
      if (tenant.tenant_name !== newName) {
        const index = clientTenants.findIndex(
          (ct) => ct.tenant_name === newName
        );
        if (index < 0) {
          setNameError('');
          return true;
        } else {
          setNameError(
            'Duplicate name already exists. Enter a different name.'
          );
          return false;
        }
      } else {
        setNameError('');
        return true;
      }
    } else {
      setNameError('The field should be at least 3 characters long');
      return false;
    }
  };

  // eslint-disable-next-line
  const handleUpdate = (e) => {
    e.preventDefault();
    let error = false;

    if (!verifyName(name)) {
      error = true;
      setNameState('error');
    }
    if (!verifyLabel(label)) {
      error = true;
      setLabelState('error');
    }

    if (!error) {
      const tenantData = {
        ...tenant,
        tenant_label: label,
        tenant_name: name,
        status,
        production,
        archival_period: archivalPeriod,
        administrator_email: administratorEmail,
      };
      updateClientTenant(tenantData).unwrap();
      setDirtyData(false);
      props.handleClose();
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    let error = false;

    if (!verifyName(name)) {
      error = true;
      setNameState('error');
    }
    if (!verifyLabel(label)) {
      error = true;
      setLabelState('error');
    }

    if (!error) {
      const tenantData = {
        tenant_label: label,
        tenant_name: name,
        status,
        production,
        archival_period: archivalPeriod,
        administrator_email: administratorEmail,
        client_id: props.clientId,
        base_locale: 'en-US',
        base_currency: 'USD',
      };

      addClientTenant(tenantData).unwrap();
      setDirtyData(false);
      props.handleClose();
    }
  };

  const productionChangeHandler = (e) => {
    setProduction(e.target.checked);
    setDirtyData(true);
  };

  const statusChangeHandler = (e) => {
    setStatus(e.target.value);
    setDirtyData(true);
  };

  const handleSubmit = props.new ? handleAdd : handleUpdate;

  const AntSwitch = withStyles(() => ({
    root: { width: 28, height: 16, padding: 0, display: 'flex' },
    switchBase: {
      padding: 2,
      color: secondaryColor[1],
      '&$checked': {
        transform: 'translateX(12px)',
        color: whiteColor,
        '& + $track': {
          opacity: 1,
          backgroundColor: primaryColor[0],
          borderColor: primaryColor[0],
        },
      },
    },
    thumb: { width: 12, height: 12, boxShadow: 'none' },
    track: {
      border: `1px solid ${primaryColor[0]}`,
      borderRadius: 8,
      opacity: 1,
      backgroundColor: whiteColor,
    },
    checked: {},
  }))(Switch);

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              helperText={labelState === 'error' ? labelError : false}
              success={labelState === 'success'}
              error={labelState === 'error'}
              id='label'
              labelText='Tenant label'
              inputProps={{
                defaultValue: label,
                onChange: (e) => {
                  if (verifyLabel(e.target.value)) {
                    setLabelState('success');
                  } else {
                    setLabelState('error');
                  }
                  setLabel(e.target.value);
                },
                autoComplete: 'off',
              }}
              formControlProps={{ fullWidth: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              helperText={nameState === 'error' ? nameError : false}
              success={nameState === 'success'}
              error={nameState === 'error'}
              id='name'
              labelText='Tenant name'
              inputProps={{
                defaultValue: name,
                onChange: (e) => {
                  if (verifyName(e.target.value)) {
                    setNameState('success');
                  } else {
                    setNameState('error');
                  }
                  setName(e.target.value);
                },
                autoComplete: 'off',
              }}
              formControlProps={{ fullWidth: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography component='div' className={classes.formControl}>
              <Grid component='label' container alignItems='center' spacing={2}>
                <Grid item>Test</Grid>
                <Grid item>
                  <AntSwitch
                    id='production'
                    label='Production'
                    checked={production}
                    onChange={productionChangeHandler}
                  />
                </Grid>
                <Grid item>Production</Grid>
              </Grid>
            </Typography>
          </Grid>
          {!props.new && status !== 'new' && (
            <Grid item xs={12}>
              <FormControl variant='standard' className={classes.formControl}>
                <InputLabel
                  id='status-select-label'
                  className={classes.inputLabel}
                >
                  Status
                </InputLabel>
                <Select
                  variant='standard'
                  className={classes.select}
                  labelId='status-select-label'
                  id='status-select'
                  value={status}
                  onChange={statusChangeHandler}
                >
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='inactive'>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomInput
              id='default'
              labelText='Archival period (days)'
              inputProps={{
                value: archivalPeriod,
                onChange: (e) => {
                  setArchivalPeriod(e.target.value);
                  setDirtyData(true);
                },
                inputComponent: integerMaskCustom,
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              id='email'
              labelText='Administrator email'
              inputProps={{
                value: administratorEmail,
                onChange: (e) => {
                  setAdministratorEmail(e.target.value);
                  setDirtyData(true);
                },
                inputComponent: emailMaskCustom,
                autoComplete: 'off',
              }}
              formControlProps={{ fullWidth: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ThemedSecondaryBtn onClick={props.handleClose}>
          Cancel
        </ThemedSecondaryBtn>
        <ThemedPrimaryBtn onClick={handleSubmit} disabled={!dirtyData}>
          {submitLabel}
        </ThemedPrimaryBtn>
      </DialogActions>
    </Dialog>
  );
}
